import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DataTableColumn } from '@/components/ui/data-table-customized'
import { SurveyRecordItem } from '../../../types'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { generatePathDatacentral, paths } from '@/utils/path'
import { UPSALE_SERVICE_MAP, UpsalePotentialValue } from '../../../constants'
import { getServiceColor } from '@/constants/serviceColors'
import HintIcon from '../../shared/hint-icon'

dayjs.extend(utc)

// created_at arrives as a naive string holding UTC, so read it as UTC before
// formatting - matches the record detail screen, which already does this.
const formatDate = (value?: string) => {
    if (!value) return '—'
    return dayjs.utc(value).local().format('DD/MM/YYYY')
}

const formatText = (value?: string | null) => value || '—'

const detailPath = (row: SurveyRecordItem) =>
    generatePathDatacentral(paths.surveyRecordDetail, {
        surveyRecordId: row?.survey_record_id,
    })

const titleWithHint = (title: string, hint: string) => (
    <span className="inline-flex items-center justify-center gap-1">
        {title}
        <HintIcon
            hint={hint}
            className="h-3 w-3"
            side="bottom"
            contentClassName="max-w-[min(28rem,calc(100vw-2rem))] whitespace-normal break-words text-left text-xs leading-relaxed"
        />
    </span>
)

export const createSurveyListColumns = (
    t: TFunction
): DataTableColumn<SurveyRecordItem>[] => [
    {
        key: 'survey_record_id',
        title: t('surveys.column.id'),
        width: 90,
        sortable: true,
        pinned: 'left',
        render: (_v, row) => (
            <Link
                to={detailPath(row)}
                className="text-blue-600 hover:underline"
            >
                {row.survey_record_id}
            </Link>
        ),
    },
    {
        key: 'customer_name',
        title: t('surveys.column.customer_name'),
        width: 180,
        sortable: true,
        render: (_v, row) => formatText(row.customer_name),
    },
    {
        key: 'employee_name',
        title: t('surveys.column.done_by'),
        width: 180,
        sortable: true,
        render: (_v, row) => formatText(row.employee_name),
    },
    {
        key: 'created_at',
        title: t('surveys.column.start_date'),
        width: 130,
        sortable: true,
        render: (_v, row) => formatDate(row.created_at),
    },
    {
        key: 'average_score',
        title: t('surveys.column.average_score'),
        width: 120,
        sortable: true,
        cellClassName: 'text-center',
        headerClassName: 'text-center',
        render: (_v, row) => formatText(row.average_score?.toString()),
    },
    {
        key: 'score_service_quality',
        title: titleWithHint(
            t('surveys.column.score_service_quality'),
            t('surveys.form.descriptions.score_service_quality')
        ),
        width: 120,
        cellClassName: 'text-center',
        headerClassName: 'text-center',
        render: (_v, row) => formatText(row.score_service_quality?.toString()),
    },
    {
        key: 'score_customer_care',
        title: titleWithHint(
            t('surveys.column.score_customer_care'),
            t('surveys.form.descriptions.score_customer_care')
        ),
        width: 120,
        cellClassName: 'text-center',
        headerClassName: 'text-center',
        render: (_v, row) => formatText(row.score_customer_care?.toString()),
    },
    {
        key: 'score_tech_support',
        title: titleWithHint(
            t('surveys.column.score_tech_support'),
            t('surveys.form.descriptions.score_tech_support')
        ),
        width: 120,
        cellClassName: 'text-center',
        headerClassName: 'text-center',
        render: (_v, row) => formatText(row.score_tech_support?.toString()),
    },
    {
        key: 'score_issue_resolution',
        title: titleWithHint(
            t('surveys.column.score_issue_resolution'),
            t('surveys.form.descriptions.score_issue_resolution')
        ),
        width: 300,
        cellClassName: 'text-center',
        headerClassName: 'text-center',
        render: (_v, row) => formatText(row.score_issue_resolution?.toString()),
    },
    {
        key: 'score_referral_willingness',
        title: titleWithHint(
            t('surveys.column.score_referral_willingness'),
            t('surveys.form.descriptions.score_referral_willingness')
        ),
        width: 300,
        cellClassName: 'text-center',
        headerClassName: 'text-center',
        render: (_v, row) =>
            formatText(row.score_referral_willingness?.toString()),
    },
    {
        key: 'upsale_potential',
        title: titleWithHint(
            t('surveys.column.upsale_potential'),
            t('surveys.form.descriptions.upsale_potential')
        ),
        width: 180,
        headerClassName: 'text-center',
        render: (_v, row) => {
            const values = row.upsale_potential
            if (!values || values.length === 0) {
                return <span className="text-muted-foreground">—</span>
            }
            return (
                <div className="flex flex-wrap gap-1">
                    {values.map((v) => {
                        const sc = getServiceColor(
                            UPSALE_SERVICE_MAP[v as UpsalePotentialValue]
                        )
                        return (
                            <span
                                key={v}
                                className="px-2 py-0.5 text-xs font-medium rounded"
                                style={{
                                    backgroundColor: sc.bg,
                                    color: sc.text,
                                }}
                            >
                                {v}
                            </span>
                        )
                    })}
                </div>
            )
        },
    },
    {
        key: 'actions',
        title: '',
        width: 60,
        pinned: 'right',
        render: (_v, row) => (
            <div className="flex justify-center w-full">
                <Button variant="ghost" size="sm" asChild>
                    <Link to={detailPath(row)}>
                        <Eye size={16} />
                    </Link>
                </Button>
            </div>
        ),
    },
]
